/**
 * Freesound Provider
 *
 * Provides access to audio files from Freesound.org, a collaborative database
 * of Creative Commons licensed sounds.
 *
 * API Documentation: https://freesound.org/docs/api/
 * Requires an API key from: https://freesound.org/apiv2/apply
 */

import { BaseProvider } from '../base/BaseProvider';
import { MediaType, ExternalAssetSource } from '../../types';
import type { IAsset, SearchParams } from '../base/types';
import type { FreesoundSearchResponse } from './types';
import { AudioAsset } from './assets';

interface FreesoundProviderConfig {
  apiKey: string;
}

/**
 * Freesound.org provider implementation
 * Provides access to free audio samples and sounds from Freesound
 */
export class FreesoundProvider extends BaseProvider {
  private static readonly BASE_URL = 'https://freesound.org/apiv2';
  private static readonly CATEGORIES = ['Music', 'Instrument samples', 'Speech', 'Sound effects', 'Soundscapes'];

  constructor(config: FreesoundProviderConfig) {
    super({
      id: ExternalAssetSource.Freesound,
      name: 'freesound',
      displayName: 'Freesound',
      description: 'Free audio samples and sounds from Freesound.org',
      baseUrl: FreesoundProvider.BASE_URL,
      apiKey: config.apiKey,
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [MediaType.Audio],
      },
    });
  }

  getCategories(): Promise<string[]> {
    return Promise.resolve([...FreesoundProvider.CATEGORIES]);
  }

  /**
   * Search for audio assets in Freesound
   */
  public async search(params: SearchParams): Promise<IAsset[]> {
    const url = new URL(`${this.baseUrl}/search/text/`);

    // Add query parameters
    if (params.query) {
      url.searchParams.append('query', params.query);
    }

    // https://freesound.org/apiv2/search/?query=music&filter=category:Music%20subcategory:"Solo%20instrument"
    if (params.category) {
      url.searchParams.append('filter', `category:"${encodeURIComponent(params.category)}"`);
    }

    // Filter by type (only audio is supported)
    if (params.types && Array.isArray(params.types) && !params.types.includes(MediaType.Audio)) {
      return [];
    }

    // Pagination
    const page = params.page || 1;
    const limit = params.limit || 20;
    url.searchParams.append('page', page.toString());
    url.searchParams.append('page_size', limit.toString());

    // Sorting - map to Freesound sort options
    if (params.sort) {
      const sortMap: Record<string, string> = {
        name: 'score',
        date: 'created_desc',
        size: 'duration_desc',
      };
      const freesoundSort = sortMap[params.sort] || 'score';
      url.searchParams.append('sort', freesoundSort);
    }

    // Fields to return
    url.searchParams.append(
      'fields',
      'id,name,description,tags,license,username,created,duration,previews,images,type,channels,filesize,bitrate,samplerate,num_downloads,avg_rating,num_ratings'
    );

    // Fetch results
    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Token ${this.apiKey}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Freesound API error: ${response.statusText}`);
    }

    const data: FreesoundSearchResponse = await response.json();

    // Convert to assets (apiKey is guaranteed to be defined here)
    return data.results.map((sound) => new AudioAsset(sound, this.apiKey!, this.baseUrl));
  }

  /**
   * Get a specific audio asset by ID
   */
  public async getAssetById(id: string): Promise<IAsset> {
    const url = `${this.baseUrl}/sounds/${id}/`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Token ${this.apiKey}`,
      },
    });

    if (!response.ok) {
      throw new Error(
        response.status === 404
          ? `Sound with ID ${id} not found in Freesound`
          : `Freesound API error: ${response.statusText}`
      );
    }

    const sound = await response.json();
    return new AudioAsset(sound, this.apiKey!, this.baseUrl);
  }
}
